import {MqttDeviceWrapper, MqttEnergyProfile, MqttMeasures} from '../mqtt';
import {
    Device,
    DeviceType,
    Measure,
    UrbanDatasetMeasureType,
} from 'smag-commons/cfg/connector/mqtt';
import {MqttUrbanDatasetMeasureTypePipe} from '../connector.pipe';
import * as _ from 'lodash';

export class UrbanDatasetMqttDeviceWrapper implements MqttDeviceWrapper<UrbanDatasetMeasureType> {

    private readonly mtp: MqttUrbanDatasetMeasureTypePipe;

    deviceType = DeviceType.URBAN_DATASET;

    maxProductionPlants = 4;

    measureTypes: Set<UrbanDatasetMeasureType>;

    constructor(private readonly devId: string,
                private readonly device: Device) {
        this.mtp = new MqttUrbanDatasetMeasureTypePipe();
        this.measureTypes = new Set([
            ...ABSORPTION_MEASURE_TYPES,
            ...INJECTION_MEASURE_TYPES,
            ..._.flatMap(PRODUCTION_MEASURE_TYPES, mt => Array.from(mt))
        ]);
    }

    getMqttMeasures(): MqttMeasures {
        const absorptionMeasures: string[] = [];
        const injectionMeasures: string[] = [];
        const productionMeasures: string[][] = [];
        for (let i = 0; i < this.maxProductionPlants; ++i) {
            productionMeasures[i] = [];
        }
        const measures = this.device.measures || {};
        for (const measId in measures) {
            const meas = measures[measId];
            if (ABSORPTION_MEASURE_TYPES.has(meas.urban_meas_type)) {
                absorptionMeasures.push(measId);
            } else if (INJECTION_MEASURE_TYPES.has(meas.urban_meas_type)) {
                injectionMeasures.push(measId);
            } else {
                for (let i = 0; i < this.maxProductionPlants; ++i) {
                    if (PRODUCTION_MEASURE_TYPES[i].has(meas.urban_meas_type)) {
                        productionMeasures[i].push(measId);
                    }
                }
            }
        }
        return {
            absorptionMeasures,
            injectionMeasures,
            productionMeasures
        };
    }

    getMeasureType(measId: string): UrbanDatasetMeasureType {
        return (this.device.measures || {})[measId]?.urban_meas_type;
    }

    setMeasureType(measId: string, measureType: UrbanDatasetMeasureType) {
        if (!this.device.measures) {
            this.device.measures = {};
        }
        if (!this.device.measures[measId]) {
            this.device.measures[measId] = {};
        }
        this.device.measures[measId].urban_meas_type = measureType;
    }

    setDefaultMeasures(profile: MqttEnergyProfile, productionPods?: string[]) {
        // NOTE: we make deep copies, as we do not want to risk overwriting the archetypes
        let measures = [];
        if (profile === MqttEnergyProfile.CONSUMER) {
            measures = _.cloneDeep(ABSORPTION_MEASURES).map(m => [m, null]);
        } else if (profile === MqttEnergyProfile.PRODUCER_PROSUMER) {
            measures = [
                ..._.cloneDeep(ABSORPTION_MEASURES).map(m => [m, 0, null]),
                ..._.cloneDeep(INJECTION_MEASURES).map(m => [m, 0, null]),
                ..._.cloneDeep(PRODUCTION_MEASURES).map(m => [m, 1, productionPods[0]])
            ];
            if (productionPods.length >= 2) {
                measures.push(..._.cloneDeep(PRODUCTION_MEASURES_2).map(m => [m, 2, productionPods[1]]));
            }
            if (productionPods.length >= 3) {
                measures.push(..._.cloneDeep(PRODUCTION_MEASURES_3).map(m => [m, 3, productionPods[2]]));
            }
            if (productionPods.length >= 4) {
                measures.push(..._.cloneDeep(PRODUCTION_MEASURES_4).map(m => [m, 4, productionPods[3]]));
            }
        }
        this.device.measures = {};
        (measures as [Measure, number, string][]).forEach(([meas, plantIndex, prodPod]) => {
            // if we do not have a production POD, we use an index in order to ensure uniqueness of `measId`
            const prodStr = plantIndex > 0 ? prodPod || `${plantIndex}` : null;
            const measId = `${this.devId}${prodStr ? '_' + prodStr : ''}_${MEASURE_SUFFIXES[meas.urban_meas_type]}`;
            this.device.measures[measId] = meas;
        });
    }

    formatMeasureType(measureType: UrbanDatasetMeasureType): string {
        return this.mtp.transform(measureType);
    }
}

const ABSORPTION_MEASURES: Measure[] = [
    {
        uom: 'kWh',
        urban_meas_type: UrbanDatasetMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY,
        expression: ''
    },
    {
        uom: 'kWh',
        urban_meas_type: UrbanDatasetMeasureType.TOTAL_ABSORBED_ENERGY,
        expression: ''
    }
];

const INJECTION_MEASURES: Measure[] = [
    {
        uom: 'kWh',
        urban_meas_type: UrbanDatasetMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY,
        expression: ''
    },
    {
        uom: 'kWh',
        urban_meas_type: UrbanDatasetMeasureType.TOTAL_INJECTED_ENERGY,
        expression: ''
    }
];

const PRODUCTION_MEASURES: Measure[] = [];

const PRODUCTION_MEASURES_2: Measure[] = [];

const PRODUCTION_MEASURES_3: Measure[] = [];

const PRODUCTION_MEASURES_4: Measure[] = [];

const ABSORPTION_MEASURE_TYPES: Set<UrbanDatasetMeasureType> = new Set(ABSORPTION_MEASURES.map(m => m.urban_meas_type));
const INJECTION_MEASURE_TYPES: Set<UrbanDatasetMeasureType> = new Set(INJECTION_MEASURES.map(m => m.urban_meas_type));
const PRODUCTION_MEASURE_TYPES: Set<UrbanDatasetMeasureType>[] = [
    new Set(PRODUCTION_MEASURES.map(m => m.urban_meas_type)),
    new Set(PRODUCTION_MEASURES_2.map(m => m.urban_meas_type)),
    new Set(PRODUCTION_MEASURES_3.map(m => m.urban_meas_type)),
    new Set(PRODUCTION_MEASURES_4.map(m => m.urban_meas_type))
];

const MEASURE_SUFFIXES: { [measType in UrbanDatasetMeasureType]: string } = {
    [UrbanDatasetMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY]: 'en_prel_15m',
    [UrbanDatasetMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY]: 'en_imm_15m',
    [UrbanDatasetMeasureType.TOTAL_ABSORBED_ENERGY]: 'en_prel_tot',
    [UrbanDatasetMeasureType.TOTAL_INJECTED_ENERGY]: 'en_imm_tot',
};
